import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Box, Grid, Paper, Button,
  Dialog, DialogTitle, DialogContent, IconButton
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { QrCode2 } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import TenantQRCodes from '../../components/TenantQRCodes';

const FacilityAdminDashboard = () => {

  const navigate = useNavigate();
  const { user, isFacilityAdmin, isAppAdmin } = useAuth();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const viewFacilityId = queryParams.get('viewFacilityId');

  const activeFacilityId = user?.facilityId || viewFacilityId;

  const [facilityTotalVisits, setFacilityTotalVisits] = useState(0);
  const [facilityPendingVisits, setFacilityPendingVisits] = useState(0);
  const [facilityHostCount, setFacilityHostCount] = useState(0);
  const [facilityAdminCount, setFacilityAdminCount] = useState(0);

  const [qrDialogOpen, setQrDialogOpen] = useState(false);

  // 🔹 Analytics Fetch
  useEffect(() => {
    if (!activeFacilityId) return;

    const fetchData = async () => {
      try {
        const visitsSnap = await getDocs(query(
          collection(db, 'visit_approvals'),
          where('facilityId', '==', activeFacilityId)
        ));
        setFacilityTotalVisits(visitsSnap.size);

        const pendingSnap = await getDocs(query(
          collection(db, 'visit_approvals'),
          where('facilityId', '==', activeFacilityId),
          where('status', '==', 'pending')
        ));
        setFacilityPendingVisits(pendingSnap.size);

        const hostSnap = await getDocs(query(
          collection(db, 'users'),
          where('facilityId', '==', activeFacilityId),
          where('role', '==', 'host')
        ));
        setFacilityHostCount(hostSnap.size);

        const adminSnap = await getDocs(query(
          collection(db, 'users'),
          where('facilityId', '==', activeFacilityId),
          where('role', '==', 'facilityadmin')
        ));
        setFacilityAdminCount(adminSnap.size);

      } catch (err) {
        console.error(err);
      }
    };

    fetchData();
  }, [activeFacilityId]);

  // 🔹 Access Guard
  if (!user || (!isFacilityAdmin() && !isAppAdmin())) {
    return null;
  }

  const actions = [
    {
      title: "Pending Approvals",
      description: "Review and manage visitor requests",
      path: "/visit-approvals",
      badge: facilityPendingVisits
    },
    {
      title: "Visit History",
      description: "View past visitor records",
      path: "/host/visits"
    },
    {
      title: "User Management",
      description: "Manage users for your facility",
      path: "/user-management"
    }
  ];

  return (
    <Box sx={{ minHeight: "100vh", background: "#fefce8", py: 4 }}>
      <Container maxWidth="lg">

        {/* HEADER */}
        <Box sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}>
          <Box>
            <Typography sx={{
              fontSize: "2rem",
              fontWeight: 900,
              fontFamily: "'Unbounded', sans-serif",
              color: "#1c1400"
            }}>
              Facility Admin Dashboard
            </Typography>

            <Typography sx={{ color: "#8a7a40", mt: 0.5 }}>
              Welcome, {user?.userName || user?.email}
            </Typography>

            <Typography sx={{
              color: "#d32f2f",
              fontWeight: 500,
              mt: 1
            }}>
              Manage users and visitor activity for your assigned facility.
            </Typography>
          </Box>

          {/* QR BUTTON */}
          <Button
            variant="contained"
            startIcon={<QrCode2 />}
            onClick={() => setQrDialogOpen(true)}
            sx={{
              background: "#1c1400",
              color: "#fde047",
              borderRadius: "8px",
              px: 3,
              "&:hover": {
                background: "#f59e0b",
                color: "#1c1400"
              }
            }}
          >
            QR Code
          </Button>
        </Box>

        {/* ANALYTICS */}
        <Paper sx={{
          p: 4,
          mb: 4,
          borderRadius: "16px",
          border: "2px solid #f0d060",
          background: "#ffffff"
        }}>
          <Typography sx={{
            fontWeight: 700,
            color: "#1c1400",
            mb: 3
          }}>
            Facility Analytics
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ color: "#8a7a40" }}>Total Visits</Typography>
              <Typography sx={{ fontSize: "2rem", fontWeight: 800, color: "#1c1400" }}>
                {facilityTotalVisits}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography sx={{ color: "#8a7a40" }}>Pending Visits</Typography>
              <Typography sx={{ fontSize: "2rem", fontWeight: 800, color: "#1c1400" }}>
                {facilityPendingVisits}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={3}>
              <Typography sx={{ color: "#8a7a40" }}>Hosts</Typography>
              <Typography sx={{ fontSize: "2rem", fontWeight: 800, color: "#1c1400" }}>
                {facilityHostCount}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* ACTION CARDS */}
        <Grid container spacing={3}>
          {actions.map((action, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Paper sx={{
                p: 3,
                borderRadius: "14px",
                border: "1px solid #f0d060",
                background: "#ffffff",
                transition: "0.3s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 10px 20px rgba(0,0,0,0.08)"
                }
              }}>
                <Typography sx={{ fontWeight: 700, color: "#1c1400" }}>
                  {action.title}
                </Typography>

                <Typography sx={{
                  color: "#6b7280",
                  fontSize: "0.9rem",
                  mt: 1
                }}>
                  {action.description}
                </Typography>

                <Button
                  fullWidth
                  sx={{
                    mt: 2,
                    background: "#1c1400",
                    color: "#fde047",
                    borderRadius: "8px",
                    "&:hover": {
                      background: "#f59e0b",
                      color: "#1c1400"
                    }
                  }}
                  onClick={() =>
                    navigate(action.path, {
                      state: { facilityId: activeFacilityId }
                    })
                  }
                >
                  Access
                </Button>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* INFO */}
        <Box sx={{ mt: 4 }}>
          <Paper sx={{
            p: 3,
            borderRadius: "12px",
            background: "#fffbea",
            border: "1px solid #f0d060"
          }}>
            <Typography sx={{ color: "#6b7280" }}>
              As a Facility Admin, you can manage users and visitor activity for your assigned facility.
            </Typography>
          </Paper>
        </Box>

        {/* QR DIALOG */}
        <Dialog
          open={qrDialogOpen}
          onClose={() => setQrDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: "16px", p: 1 }
          }}
        >
          <DialogTitle sx={{
            fontWeight: 700,
            color: "#1c1400",
            display: "flex",
            justifyContent: "space-between"
          }}>
            Facility QR Code
            <IconButton onClick={() => setQrDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ textAlign: "center" }}>
            <TenantQRCodes facilityId={activeFacilityId} />
          </DialogContent>
        </Dialog>

      </Container>
    </Box>
  );
};

export default FacilityAdminDashboard;